<script lang="ts">
  import { CheckCircle, XCircle } from 'lucide-svelte';
  import { scale, fade } from 'svelte/transition';
  import type { ExerciseItem, EvaluationResult, UserAnswer, MatchPairsAnswer } from '../../types/exercise';
  import { _ } from 'svelte-i18n';

  interface Props {
    exercise: ExerciseItem;
    showFeedback: boolean;
    evaluationResult: EvaluationResult | null;
    onAnswerChange: (answer: UserAnswer) => void;
  }

  let { exercise, showFeedback, evaluationResult, onAnswerChange }: Props = $props();

  // Local state
  let selectedLeft = $state<string | null>(null);
  let userPairs = $state<Record<string, string>>({});
  let matchedPairs = $state<Set<string>>(new Set());

  // Get mode-specific data
  let modeData = $derived(
    exercise.modeData.type === 'MATCH_PAIRS' ? exercise.modeData : null
  );

  // Check if all pairs are matched
  let allMatched = $derived(
    modeData ? Object.keys(userPairs).length === modeData.leftItems.length : false
  );

  // Reset state when exercise changes
  $effect(() => {
    if (exercise.id) {
      selectedLeft = null;
      userPairs = {};
      matchedPairs = new Set();
    }
  });

  // Update parent when pairs change
  $effect(() => {
    if (Object.keys(userPairs).length > 0) {
      const answer: MatchPairsAnswer = {
        type: 'MATCH_PAIRS',
        pairs: userPairs,
      };
      onAnswerChange(answer);
    }
  });

  // Handle left item click
  function handleLeftClick(id: string) {
    if (showFeedback || matchedPairs.has(id)) return;
    selectedLeft = selectedLeft === id ? null : id;
  }

  // Handle right item click
  function handleRightClick(id: string) {
    if (showFeedback || !selectedLeft) return;

    // Check if this right item is already paired
    const existingPair = Object.entries(userPairs).find(([_, rightId]) => rightId === id);
    if (existingPair) return;

    // Create the pair
    userPairs = { ...userPairs, [selectedLeft]: id };
    matchedPairs = new Set([...matchedPairs, selectedLeft]);
    selectedLeft = null;
  }

  // Check if a pair is correct (for feedback display)
  function isPairCorrect(leftId: string): boolean {
    if (!modeData) return false;
    const userRightId = userPairs[leftId];
    return modeData.correctPairs[leftId] === userRightId;
  }

  // Get item styles
  function getLeftItemClass(id: string): string {
    const base = 'p-4 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer font-medium relative overflow-hidden text-sm md:text-base';
    const defaultBg = 'bg-card hover:bg-accent';

    if (showFeedback) {
      if (matchedPairs.has(id)) {
        return isPairCorrect(id)
          ? `${base} border-green-500 bg-green-100 dark:bg-green-500/40 text-green-900 dark:text-green-50`
          : `${base} border-red-500 bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100`;
      }
      return `${base} border-red-500 bg-red-100 dark:bg-red-900/40 opacity-50`;
    }

    if (matchedPairs.has(id)) {
      return `${base} border-primary bg-primary/80 dark:bg-primary/60 text-primary-foreground opacity-60 pointer-events-none`;
    }

    if (selectedLeft === id) {
      return `${base} border-primary bg-primary/90 dark:bg-primary/70 text-primary-foreground ring-4 ring-primary/30 scale-105 shadow-lg z-10`;
    }

    return `${base} border-border/50 ${defaultBg} hover:border-primary/50 hover:scale-[1.02]`;
  }

  function getRightItemClass(id: string): string {
    const base = 'p-4 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer font-medium relative overflow-hidden text-sm md:text-base';
    const defaultBg = 'bg-card hover:bg-accent';

    // Check if this right item is already used
    const isUsed = Object.values(userPairs).includes(id);

    if (showFeedback) {
      if (isUsed) {
        // Find which left item this is paired with
        const leftId = Object.entries(userPairs).find(([_, rightId]) => rightId === id)?.[0];
        if (leftId && isPairCorrect(leftId)) {
          return `${base} border-green-500 bg-green-100 dark:bg-green-500/40 text-green-900 dark:text-green-50`;
        }
        return `${base} border-red-500 bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100`;
      }
      return `${base} border-border opacity-50 ${defaultBg}`;
    }

    if (isUsed) {
      return `${base} border-primary bg-primary/80 dark:bg-primary/60 text-primary-foreground opacity-60 pointer-events-none`;
    }

    if (selectedLeft) {
      return `${base} border-dashed border-primary/60 hover:border-solid hover:border-primary hover:bg-primary/90 dark:hover:bg-primary/70 hover:text-primary-foreground hover:scale-[1.02]`;
    }

    return `${base} border-border/50 ${defaultBg}`;
  }
</script>

<div class="space-y-8">
  <!-- Instructions -->
  <div class="text-center" in:scale={{ duration: 300, start: 0.95 }}>
    <h2 class="text-lg font-semibold text-muted-foreground">
      {$_('exercise.match_pairs_by_clicking')}
    </h2>
  </div>

  <!-- Matching columns -->
  {#if modeData}
    <div class="grid grid-cols-2 gap-4 md:gap-6">
      <!-- Left column -->
      <div class="space-y-3">
        {#each modeData.leftItems as item}
          <button
            type="button"
            onclick={() => handleLeftClick(item.id)}
            disabled={showFeedback || matchedPairs.has(item.id)}
            class={getLeftItemClass(item.id)}
          >
            <span>{item.text}</span>
            {#if showFeedback && matchedPairs.has(item.id)}
              {#if isPairCorrect(item.id)}
                <div in:scale={{ duration: 200 }} class="absolute top-1 right-1">
                  <CheckCircle class="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
              {:else}
                <div in:scale={{ duration: 200 }} class="absolute top-1 right-1">
                  <XCircle class="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
              {/if}
            {/if}
          </button>
        {/each}
      </div>

      <!-- Right column -->
      <div class="space-y-3">
        {#each modeData.rightItems as item}
          <button
            type="button"
            onclick={() => handleRightClick(item.id)}
            disabled={showFeedback || Object.values(userPairs).includes(item.id)}
            class={getRightItemClass(item.id)}
          >
            <span>{item.text}</span>
          </button>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Status indicator -->
  {#if !showFeedback}
    <div class="text-center text-sm font-medium text-muted-foreground h-6">
      {#if selectedLeft}
        <span class="text-primary animate-pulse">{$_('exercise.select_matching_word_right')}</span>
      {:else if !allMatched}
        {$_('exercise.click_left_word_to_start')}
      {:else}
        <span class="text-green-500 font-bold">{$_('exercise.all_words_paired')}</span>
      {/if}
    </div>
  {/if}

  <!-- Feedback -->
  {#if showFeedback && evaluationResult}
    <div
      in:scale={{ duration: 300, start: 0.95 }}
      class="p-5 rounded-xl shadow-lg border-l-4 backdrop-blur-sm
        {evaluationResult.isCorrect
        ? 'bg-green-100/80 dark:bg-green-500/30 border-green-500 text-green-900 dark:text-green-50'
        : 'bg-red-100/80 dark:bg-red-500/30 border-red-500 text-red-900 dark:text-red-50'}"
    >
      <div class="flex items-center gap-3">
        {#if evaluationResult.isCorrect}
          <div class="p-1 bg-green-200 dark:bg-green-800 rounded-full">
            <CheckCircle class="w-6 h-6 text-green-700 dark:text-green-300" />
          </div>
          <span class="font-bold text-lg">{$_('exercise.all_pairs_correct')}</span>
        {:else}
          <div class="p-1 bg-red-200 dark:bg-red-800 rounded-full">
            <XCircle class="w-6 h-6 text-red-700 dark:text-red-300" />
          </div>
          <span class="font-bold text-lg">{evaluationResult.feedback}</span>
        {/if}
      </div>
    </div>
  {/if}
</div>
